import React from 'react';
import PropTypes from 'prop-types';
import WithTooltip from 'relements/build/esm/overlays/withTooltip';
import Button from 'relements/build/esm/ui/button';
import ContextMenu from 'relements/build/esm/overlays/contextMenu';
import Loader from 'relements/build/esm/decorators/loader';
import RelementsIcon from 'relements/build/esm/ui/icon';

import hashHistory from '@src/History';
import { createAlert } from '@src/actions/BotActions';
import { isNewPipeline } from '@src/actions/_utils';
import { setCurrentScreen } from '@src/actions/StoryActions';
import Icon from '@src/components/Icon';
import SupermanIcon from '@src/components/Icon/superman.svg';
import { SCREEN_TYPES } from '@src/constants/MogamboConstants';
import connection from '@src/connection';
import { isProduction } from '@src/utils';
import { getBusinessAuditHeaders } from '@src/containers/BusinessManagerV2/utils';

import GeneralSettings from './components/GeneralSettings';
import PartnerSpecificSettings from './components/PartnerSpecificSettings';
import BotPromptTriggerSettings from './components/BotPromptTriggerSettings';

import './BusinessManager.scss';
import * as maps from './maps';

import { TRANSFERS } from './const';
import { canTransfer, transfer } from './utils';

@connection(maps.listenToStores, maps.getStateFromStores)
@Loader({})
export default class BusinessManager extends React.Component {
  TAB_PATHS = {
    GENERAL: 'general-settings',
    PARTNER: 'partner-specific-settings',
    TRIGGERS: 'bot-prompt-triggers-settings',
  };

  TABS = () => [
    {
      title: 'General Settings',
      icon: 'business',
      path: this.TAB_PATHS.GENERAL,
      key: 'generalSettings',
    },
    {
      title: 'Account Specific',
      icon: 'partner',
      path: this.TAB_PATHS.PARTNER,
      key: 'partnerSpecificSettings',
    },
    {
      title: 'Bot Prompt Triggers',
      icon: 'prompt-trigger',
      path: this.TAB_PATHS.TRIGGERS,
      isNew: this.props.viaName === 'new',
      key: 'botPromptTriggers',
    },
  ];

  state = {
    currentTab: this.TAB_PATHS.GENERAL,
    isNew: this.props.viaName === 'new',
  };

  componentDidMount() {
    setCurrentScreen(SCREEN_TYPES.BUSINESS_MANAGER);
    const { viaName } = this.props;
    this.props.activateLoader();
    this.props.getBusinessData(viaName).then(this.props.deactivateLoader);
    // Since validation errors are stored in the Store, if there are existing validation errors and move to
    // creating a new one, the validation errors of the older business show up.
    // Hence, we clear the validation errors whenever the component mounts so that the previous validation errors
    // are ignored.
    this.props.clearBusinessDataValidationErrors();
  }

  componentDidUpdate(prevProps) {
    if (this.props.viaName !== prevProps.viaName) {
      this.props.activateLoader();
      this.props
        .getBusinessData(this.props.viaName)
        .then(this.props.deactivateLoader);
      this.props.clearBusinessDataValidationErrors();
    }
  }

  render() {
    const { generalSettings = {} } = this.props;
    const isSuperman = isNewPipeline({
      pipeline_version: generalSettings.pipelineVersion,
    });
    return (
      <div className="business-manager">
        <div className="business-manager-header">
          <div className="business-manager-header-lhs">
            Business Manager 
            {' '}
            <span>→</span>
            <span>
              {this.state.isNew
                ? 'New Business'
                : `${generalSettings.businessViaName || ''}`}
            </span>
            {isSuperman ? (
              <RelementsIcon
                className="superman"
                src={SupermanIcon}
                tooltip="Superman Pipeline"
                tooltipPosition="BOTTOM"
              />
            ) : null}
          </div>
          <div className="business-manager-header-rhs">
            {this.renderTransferDropdown()}
            {this.renderSaveButton()}
          </div>
        </div>
        <div className="business-manager-content">
          <div className="business-manager-menu">
            {this.TABS().map(({ title, icon, path, isNew, key }) =>
              this.renderMenuItem(title, icon, path, isNew, key)
            )}
          </div>
          <div className="business-manager-body">{this.renderBody()}</div>
        </div>
      </div>
    );
  }

  renderTransferDropdown = () => {
    if (this.state.isNew || isProduction()) return null;
    return (
      <ContextMenu.Button
        ref={(contextMenuButtonRef) => {
          this._contextMenuButtonRef = contextMenuButtonRef;
        }}
        size="big"
        title="Publish"
        offset={{ top: 44, left: -80 }}
      >
        <ContextMenu.Item onClick={this.onTransferBusinessClicked} size="big">
          Publish Business
        </ContextMenu.Item>
        <ContextMenu.Item onClick={this.onTransferAllTasksClicked} size="big">
          Publish All Menu Options
        </ContextMenu.Item>
        <ContextMenu.Item onClick={this.onTransferAllPromptTriggers} size="big">
          Publish All Prompt Triggers
        </ContextMenu.Item>
        <ContextMenu.Item onClick={this.onTransferAllClicked} size="big">
          Publish All
        </ContextMenu.Item>
      </ContextMenu.Button>
    );
  };

  renderSaveButton = () => {
    return (
      <Button
        type={Button.TYPES.PRIMARY}
        onClick={this.onSaveBusinessClicked}
        size={Button.SIZES.BIG}
        disabled={!this.props.businessDataEdited}
      >
        Save
      </Button>
    );
  };

  renderMenuItem(title, icon, path, disabled, key) {
    const activeClassName = path === this.state.currentTab ? 'active' : '';
    const disabledClassName = disabled ? 'disabled' : '';

    const errorDict = this.props.allErrors[key];
    const errorKeys = errorDict ? Object.keys(errorDict) : [];
    const errors = errorKeys.map((k) => errorDict[k]);
    const numErrors = errors.length;
    const errorClassName = numErrors ? 'error' : '';

    let tooltip = null;
    if (disabled) tooltip = 'Save this business first';
    else if (numErrors.length === 1)
      tooltip = errors.map((error) => error.message).join('<br/>');

    return (
      <div
        onClick={!disabled && (() => this.setState({ currentTab: path }))}
        className={`edit-business-menu-item-wrapper ${activeClassName} ${errorClassName}`}
      >
        <WithTooltip tooltip={tooltip}>
          <div className={`edit-business-menu-item ${disabledClassName}`}>
            <div className="edit-business-menu-item-icon">
              <Icon iconType={icon} size="24px" />
            </div>
            <div className="edit-business-menu-item-title">{title}</div>
            {numErrors ? (
              <div className="edit-business-menu-item-error-count">
                {numErrors}
              </div>
            ) : null}
          </div>
        </WithTooltip>
      </div>
    );
  }

  renderBody() {
    let meta;
    switch (this.state.currentTab) {
      case this.TAB_PATHS.GENERAL:
        if (!this.props.generalSettings) return null;
        meta = {
          allBots: this.props.allBots,
          allTeams: this.props.allTeams,
          allPartners: this.props.allPartners,
          allLanguages: this.props.allLanguages,
          allLacts: this.props.allLacts,
          isNew: this.state.isNew,
          id: this.props.id,
        };
        return (
          <GeneralSettings
            value={this.props.generalSettings}
            onChange={this.handleChange('generalSettings')}
            meta={meta}
            errors={this.props.allErrors.generalSettings}
          />
        );
      case this.TAB_PATHS.TRIGGERS:
        if (!this.props.botPromptTriggerSettings) return null;
        meta = { isNew: this.state.isNew };
        return (
          <BotPromptTriggerSettings
            value={this.props.botPromptTriggerSettings}
            onChange={this.handleChange('botPromptTriggerSettings')}
            meta={meta}
            errors={this.props.allErrors.botPromptTriggerSettings}
          />
        );
      case this.TAB_PATHS.PARTNER:
        if (!this.props.partnerSpecificSettings) return null;
        meta = {
          isNew: this.state.isNew,
          businessId: this.props.generalSettings.id,
          allPlatforms: this.props.allPlatforms,
          activePartners: this.props.generalSettings.activePartners,
          allClients: this.props.allClients,
          reversePartnerIdMap: this.props.reversePartnerIdMap,
        };
        return (
          <PartnerSpecificSettings
            value={this.props.partnerSpecificSettings}
            onChange={this.handleChange('partnerSpecificSettings')}
            onTransferTask={this.onTransferTaskClicked}
            meta={meta}
            errors={this.props.allErrors.partnerSpecificSettings}
          />
        );
      default:
        return null;
    }
  }

  handleChange = (key) => (value) => {
    this.props.editBusinessData(key, value);
  };

  onSaveBusinessClicked = async () => {
    if (!this.props.businessDataEdited) {
      createAlert('No changes to save', null, 'NEGATIVE');
      return;
    }

    this.props.activateLoader();

    const errors = await this.props.validateBusinessData();
    if (errors.length > 0) {
      this.props.deactivateLoader();
      createAlert(errors[0].message, null, 'NEGATIVE');
    } else {
      this.props
        .saveBusinessData({ isNew: this.state.isNew })
        .then((response) => {
          createAlert('Changes saved successfully', null, 'POSITIVE');
          const viaName = response[0].body.via_name;
          if (this.state.isNew) {
            this.setState({ isNew: false });
          }
          hashHistory.push(`${viaName}`);
          this.props.activateLoader();
          this.props.getBusinessData(viaName).then(this.props.deactivateLoader);
        })
        .catch((e) => {
          createAlert(e, null, 'NEGATIVE');
        })
        .finally(this.props.deactivateLoader);
    }
  };

  onTransferBusinessClicked = (e) => {
    const { businessName, id, activePartners } = this.props.generalSettings;
    const { activateLoader, deactivateLoader } = this.props;
    this._contextMenuButtonRef.handleClose(e);
    if (!canTransfer(['business'])) return;

    const headers = getBusinessAuditHeaders(
      id,
      'transfer_business',
      activePartners
    );
    transfer(
      { ...TRANSFERS.BUSINESS(businessName), activateLoader, deactivateLoader },
      [
        {
          api: this.props.transferBusiness,
          params: [id],
          headers,
        },
      ]
    );
  };

  onTransferAllPromptTriggers = (e) => {
    const { triggerIdList, id, activePartners } = this.props.generalSettings;
    const { activateLoader, deactivateLoader } = this.props;
    this._contextMenuButtonRef.handleClose(e);
    if (!canTransfer(['trigger'])) return;

    const headers = getBusinessAuditHeaders(
      id,
      'transfer_bot_prompts',
      activePartners
    );
    transfer({ ...TRANSFERS.TRIGGERS(), activateLoader, deactivateLoader }, [
      {
        api: this.props.transferPromptTriggers,
        params: [triggerIdList],
        headers,
      },
    ]);
  };

  onTransferAllTasksClicked = (e) => {
    const { allTasks } = this.props.partnerSpecificSettings;
    const { businessName, id, activePartners } = this.props.generalSettings;
    const { activateLoader, deactivateLoader } = this.props;
    const taskIds = allTasks.map((task) => task.id);
    this._contextMenuButtonRef.handleClose(e);
    if (!canTransfer(['task'], taskIds)) return;

    const headers = getBusinessAuditHeaders(
      id,
      'transfer_all_task',
      activePartners
    );
    transfer(
      { ...TRANSFERS.TASKS(businessName), activateLoader, deactivateLoader },
      [
        {
          api: this.props.transferTasks,
          params: [taskIds],
          headers,
        },
      ]
    );
  };

  onTransferTaskClicked = (taskId, e) => {
    const { id, activePartners } = this.props.generalSettings;
    const { activateLoader, deactivateLoader } = this.props;
    this._contextMenuButtonRef.handleClose(e);
    if (!canTransfer(['task'], [taskId])) return;

    const headers = getBusinessAuditHeaders(
      id,
      'transfer_task',
      activePartners
    );
    transfer(
      {
        ...TRANSFERS.SINGLE_TASK(),
        activateLoader,
        deactivateLoader,
      },
      [
        {
          api: this.props.transferTasks,
          params: [taskId],
          headers,
        },
      ]
    );
  };

  onTransferAllClicked = (e) => {
    const {
      businessName,
      id,
      triggerIdList,
      activePartners,
    } = this.props.generalSettings;
    const { allTasks } = this.props.partnerSpecificSettings;
    const { activateLoader, deactivateLoader } = this.props;
    this._contextMenuButtonRef.handleClose(e);
    if (!canTransfer(['task', 'trigger', 'business'], allTasks)) return;

    const businessHeaders = getBusinessAuditHeaders(
      id,
      'transfer_business',
      activePartners
    );
    const promptHeaders = getBusinessAuditHeaders(
      id,
      'transfer_bot_prompts',
      activePartners
    );
    const taskHeaders = getBusinessAuditHeaders(
      id,
      'transfer_all_task',
      activePartners
    );
    transfer(
      { ...TRANSFERS.ALL(businessName), activateLoader, deactivateLoader },
      [
        {
          api: this.props.transferBusiness,
          params: [id],
          headers: businessHeaders,
        },
        {
          api: this.props.transferPromptTriggers,
          params: [triggerIdList],
          headers: promptHeaders,
        },
        {
          api: this.props.transferTasks,
          params: [allTasks.map((task) => task.id)],
          headers: taskHeaders,
        },
      ]
    );
  };
}

BusinessManager.propTypes = {
  botPromptTriggerSettings: PropTypes.object,
  generalSettings: PropTypes.object,
  partnerSpecificSettings: PropTypes.object,

  allBots: PropTypes.arrayOf(PropTypes.object),

  allClients: PropTypes.arrayOf(PropTypes.object),
  allLanguages: PropTypes.arrayOf(PropTypes.object),
  allErrors: PropTypes.arrayOf(PropTypes.object),
  allPartners: PropTypes.arrayOf(PropTypes.object),
  allPlatforms: PropTypes.arrayOf(PropTypes.object),
  allTeams: PropTypes.arrayOf(PropTypes.object),

  activateLoader: PropTypes.func,
  deactivateLoader: PropTypes.func,

  getBusinessData: PropTypes.func,
  editBusinessData: PropTypes.func,
  clearBusinessDataValidationErrors: PropTypes.func,
  validateBusinessData: PropTypes.func,
  saveBusinessData: PropTypes.func,

  transferBusiness: PropTypes.func,
  transferPromptTriggers: PropTypes.func,
  transferTasks: PropTypes.func,

  businessDataEdited: PropTypes.bool,

  viaName: PropTypes.string,
  reversePartnerIdMap: PropTypes.object,
};

BusinessManager.defaultProps = {
  botPromptTriggerSettings: {},
  generalSettings: {},
  partnerSpecificSettings: {},

  allBots: [],
  allErrors: [],
  allPartners: [],
  allPlatforms: [],
  allTeams: [],
  allLanguages: [],
  allClients: [],

  activateLoader: () => {},
  deactivateLoader: () => {},
  getBusinessData: () => {},
  editBusinessData: () => {},
  clearBusinessDataValidationErrors: () => {},
  validateBusinessData: () => {},
  saveBusinessData: () => {},
  transferBusiness: () => {},
  transferPromptTriggers: () => {},
  transferTasks: () => {},

  businessDataEdited: false,

  viaName: '',
  reversePartnerIdMap: {},
};
